import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Select from "react-select";
import DatePickerModal from '../components/DatePickerModal/DatePickerModal';
import './GestioneOrdini.css';
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const STATE_LABELS = {
    da_fare: "Da fare",
    da_completare: "Da completare",
    completato: "Completato"
};

const STATE_CLASSES = {
    da_fare: "state-da-fare",
    da_completare: "state-da-completare",
    completato: "state-completato"
};

// Filtro stato: multi-select sui 3 stati logici. Default seleziona Da fare + Da completare
// (= nasconde i completati). Selezione vuota = nessun filtro (mostra tutto).
const STATE_FILTER_OPTIONS = [
    { value: "da_fare", label: "Da fare" },
    { value: "da_completare", label: "Da completare" },
    { value: "completato", label: "Completato" }
];
const DEFAULT_STATE_FILTER = [STATE_FILTER_OPTIONS[0], STATE_FILTER_OPTIONS[1]];
const stateValuesToCsv = (arr) => (arr || []).map(o => o.value).join(',');

const GestioneOrdini = () => {
    const navigate = useNavigate();

    // Data
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);

    // UI state
    const [showInfo, setShowInfo] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [toast, setToast] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const totalPages = Math.ceil(total / limit);

    // Sorting
    const [sortKey, setSortKey] = useState("scheduled_date");
    const [sortDir, setSortDir] = useState("desc");

    // Search
    const [searchText, setSearchText] = useState("");

    // Filters
    const [selectedStateFilter, setSelectedStateFilter] = useState(DEFAULT_STATE_FILTER);
    const getToday = () => new Date().toISOString().split('T')[0];
    const [dateFrom, setDateFrom] = useState(getToday());
    const [dateTo, setDateTo] = useState(getToday());

    // Applied filters: di default escludo i completati e mostro solo gli ordini di oggi
    const [appliedFilters, setAppliedFilters] = useState({
        state: stateValuesToCsv(DEFAULT_STATE_FILTER),
        dateFrom: getToday(),
        dateTo: getToday(),
        search: ""
    });

    // Filter options
    const [filterOptions, setFilterOptions] = useState({
        states: []
    });

    // Detail modal
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailOrder, setDetailOrder] = useState(null);
    const [detailMoves, setDetailMoves] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    // Quantity editing in detail modal
    // editedQuantities: input pendente (non ancora salvato).
    // Il valore "salvato" vive in move.our_quantity (arrivato dal backend o aggiornato post-save).
    const [editedQuantities, setEditedQuantities] = useState({});
    const [savingMoves, setSavingMoves] = useState({});
    const [savingDraft, setSavingDraft] = useState(false);
    const [validating, setValidating] = useState(false);

    // Confirm dialog generico (avvisi/conferme della convalida)
    const [confirmDialog, setConfirmDialog] = useState(null);

    const showToast = useCallback((type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/odoo-ordini/orders`, {
                params: {
                    page,
                    limit,
                    state: appliedFilters.state,
                    dateFrom: appliedFilters.dateFrom,
                    dateTo: appliedFilters.dateTo,
                    search: appliedFilters.search
                }
            });
            setData(res.data.data || []);
            setTotal(res.data.total || 0);

            if (res.data.filters) {
                setFilterOptions({
                    states: res.data.filters.states || []
                });
            }
        } catch (err) {
            console.error("Errore nel caricamento ordini:", err);
            showToast("error", "Errore nel caricamento dei dati");
        } finally {
            setLoading(false);
        }
    }, [page, limit, appliedFilters, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Sort handler
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sortedData = [...data].sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];

        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    // Apply filters
    const handleApplyFilters = () => {
        setAppliedFilters({
            state: stateValuesToCsv(selectedStateFilter),
            dateFrom,
            dateTo,
            search: searchText
        });
        setPage(1);
    };

    const handleResetFilters = () => {
        setSelectedStateFilter(DEFAULT_STATE_FILTER);
        setDateFrom(getToday());
        setDateTo(getToday());
        setSearchText("");
        setAppliedFilters({
            state: stateValuesToCsv(DEFAULT_STATE_FILTER),
            dateFrom: getToday(),
            dateTo: getToday(),
            search: ""
        });
        setPage(1);
    };

    // Detail modal
    const handleOpenDetail = async (order) => {
        setDetailOrder(order);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setEditedQuantities({});
        setSavingMoves({});

        try {
            const res = await axios.get(`${API_URL}/odoo-ordini/order-detail/${order.id}`);
            setDetailMoves(res.data.moves || []);
        } catch (err) {
            console.error("Errore nel caricamento dettaglio:", err);
            showToast("error", "Errore nel caricamento del dettaglio ordine");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setDetailModalOpen(false);
        setDetailOrder(null);
        setDetailMoves([]);
        setEditedQuantities({});
        setSavingMoves({});
    };

    // Quantity editing
    const handleQuantityChange = (moveId, value) => {
        setEditedQuantities(prev => ({ ...prev, [moveId]: value }));
    };

    const handleSaveQuantity = async (move) => {
        const newQtyDisplay = parseFloat(editedQuantities[move.id]);
        if (isNaN(newQtyDisplay)) {
            showToast("error", "Inserire un valore numerico valido");
            return;
        }

        // Se la move ha unita secondaria attiva, l'input e' in secondaria -> converti a primaria.
        // Il nostro DB salva sempre in primaria per coerenza.
        // Usiamo secondary_uom_factor (vero rapporto da product.secondary.unit) e non unit_factor
        // di stock.move che e' sempre 1.
        const isSecondary = parseFloat(move.secondary_uom_qty || 0) > 0;
        const factor = parseFloat(move.secondary_uom_factor) > 0 ? parseFloat(move.secondary_uom_factor) : 1;
        const newQtyPrimary = isSecondary ? newQtyDisplay * factor : newQtyDisplay;

        setSavingMoves(prev => ({ ...prev, [move.id]: true }));
        try {
            const res = await axios.post(`${API_URL}/odoo-ordini/update-quantity`, {
                moveId: move.id,
                quantityDone: newQtyPrimary,
                pickingId: detailOrder?.id,
                pickingName: detailOrder?.name
            });

            if (res.data.success) {
                showToast("success", `Quantita aggiornata per "${move.product_name}"`);
                // Aggiorno our_quantity (sempre in primaria) sul move locale
                setDetailMoves(prev => prev.map(m =>
                    m.id === move.id ? { ...m, our_quantity: newQtyPrimary } : m
                ));
                // Ricalcolo lo stato logico: da_completare se almeno un move ha qty primaria > 0
                if (detailOrder && detailOrder.display_state !== 'completato') {
                    const willHaveAnyQty = detailMoves.some(m => {
                        const q = m.id === move.id ? newQtyPrimary : (m.our_quantity != null ? parseFloat(m.our_quantity) : 0);
                        return q > 0;
                    });
                    const newDisplayState = willHaveAnyQty ? 'da_completare' : 'da_fare';
                    setDetailOrder(prev => prev ? { ...prev, display_state: newDisplayState } : prev);
                    setData(prev => prev.map(r =>
                        r.id === detailOrder.id && r.display_state !== 'completato'
                            ? { ...r, display_state: newDisplayState }
                            : r
                    ));
                }
                setEditedQuantities(prev => {
                    const next = { ...prev };
                    delete next[move.id];
                    return next;
                });
            }
        } catch (err) {
            console.error("Errore aggiornamento quantita:", err);
            showToast("error", err.response?.data?.details || "Errore nell'aggiornamento");
        } finally {
            setSavingMoves(prev => ({ ...prev, [move.id]: false }));
        }
    };

    // Salva in bozza: applica tutte le quantita editate non ancora salvate (loop su update-quantity).
    // Ogni save aggiorna il nostro DB (quantita_gestite + ordini_gestiti). Niente Odoo.
    const handleSaveDraft = async () => {
        const entries = Object.entries(editedQuantities);
        if (entries.length === 0) return;

        setSavingDraft(true);
        let ok = 0, ko = 0;
        const savedQtyMap = {}; // mappa moveId -> qty PRIMARIA salvata

        for (const [moveIdStr, val] of entries) {
            const moveId = parseInt(moveIdStr);
            const newQtyDisplay = parseFloat(val);
            if (isNaN(newQtyDisplay)) { ko++; continue; }

            // Conversione display->primaria se la move ha unita secondaria attiva
            const move = detailMoves.find(m => m.id === moveId);
            const isSecondary = move && parseFloat(move.secondary_uom_qty || 0) > 0;
            const factor = move && parseFloat(move.secondary_uom_factor) > 0 ? parseFloat(move.secondary_uom_factor) : 1;
            const newQtyPrimary = isSecondary ? newQtyDisplay * factor : newQtyDisplay;

            try {
                const res = await axios.post(`${API_URL}/odoo-ordini/update-quantity`, {
                    moveId,
                    quantityDone: newQtyPrimary,
                    pickingId: detailOrder?.id,
                    pickingName: detailOrder?.name
                });
                if (res.data.success) {
                    ok++;
                    savedQtyMap[moveId] = newQtyPrimary;
                    setDetailMoves(prev => prev.map(m =>
                        m.id === moveId ? { ...m, our_quantity: newQtyPrimary } : m
                    ));
                } else {
                    ko++;
                }
            } catch (err) {
                ko++;
            }
        }

        if (ok > 0) {
            setEditedQuantities({});
            // Ricalcolo lo stato logico: da_completare se almeno un move ha qty > 0, altrimenti da_fare
            if (detailOrder && detailOrder.display_state !== 'completato') {
                const willHaveAnyQty = detailMoves.some(m => {
                    const q = savedQtyMap[m.id] !== undefined
                        ? savedQtyMap[m.id]
                        : (m.our_quantity != null ? parseFloat(m.our_quantity) : 0);
                    return q > 0;
                });
                const newDisplayState = willHaveAnyQty ? 'da_completare' : 'da_fare';
                setDetailOrder(prev => prev ? { ...prev, display_state: newDisplayState } : prev);
                setData(prev => prev.map(r =>
                    r.id === detailOrder.id && r.display_state !== 'completato'
                        ? { ...r, display_state: newDisplayState }
                        : r
                ));
            }
        }

        setSavingDraft(false);

        if (ko === 0) {
            showToast("success", `Bozza salvata (${ok} righe)`);
        } else if (ok === 0) {
            showToast("error", `Errore nel salvataggio bozza (${ko} righe fallite)`);
        } else {
            showToast("error", `${ok} salvate, ${ko} fallite`);
        }
    };

    // Convalida finale: pre-check su edit non salvate e quantita 0, poi chiama il backend.
    const handleValidate = () => {
        // Step 1: edit non salvate -> avviso bloccante
        if (Object.keys(editedQuantities).length > 0) {
            setConfirmDialog({
                kind: 'info',
                title: 'Modifiche non salvate',
                message: 'Hai delle quantita modificate ma non ancora salvate. Salvale in bozza prima di convalidare.',
                confirmLabel: 'Ho capito',
                onConfirm: () => setConfirmDialog(null)
            });
            return;
        }

        // Step 2: quantita a 0 -> conferma
        const zeroCount = detailMoves.filter(m => {
            const q = m.our_quantity != null ? parseFloat(m.our_quantity) : 0;
            return !(q > 0);
        }).length;

        if (zeroCount > 0) {
            setConfirmDialog({
                kind: 'warning',
                title: `${zeroCount} prodotti con quantita 0`,
                message: `Ci sono ${zeroCount} righe con quantita a 0. L'ordine verra chiuso senza creare un residuo. Vuoi procedere?`,
                confirmLabel: 'Convalida comunque',
                cancelLabel: 'Annulla',
                onConfirm: () => {
                    setConfirmDialog(null);
                    executeValidate();
                },
                onCancel: () => setConfirmDialog(null)
            });
            return;
        }

        executeValidate();
    };

    const executeValidate = async () => {
        if (!detailOrder) return;
        setValidating(true);
        try {
            const res = await axios.post(`${API_URL}/odoo-ordini/validate/${detailOrder.id}`);
            if (res.data.success) {
                showToast("success", `Ordine "${detailOrder.name}" convalidato`);
                setData(prev => prev.map(r =>
                    r.id === detailOrder.id ? { ...r, display_state: 'completato' } : r
                ));
                handleCloseDetail();
                fetchData();
            } else {
                showToast("error", res.data.error || "Convalida fallita");
            }
        } catch (err) {
            console.error("Errore convalida:", err);
            showToast("error", err.response?.data?.details || err.response?.data?.error || "Errore convalida");
        } finally {
            setValidating(false);
        }
    };

    // Stampa etichetta: apre una popup col template etichetta LOCAL toyou e auto-stampa.
    // Le immagini stanno in frontend/public/etichetta/. JsBarcode renderizza il barcode
    // dentro la popup via CDN.
    const handlePrintBarcode = (move) => {
        if (!move.barcode) {
            showToast("error", "Nessun barcode disponibile per questo prodotto");
            return;
        }

        // Calcolo della quantita da stampare (stessa logica dell'input del modal):
        // se l'utente sta editando, uso il valore in input; altrimenti uso il salvato.
        const editedRaw = editedQuantities[move.id];
        const isSecondary = parseFloat(move.secondary_uom_qty || 0) > 0;
        const factor = parseFloat(move.secondary_uom_factor) > 0 ? parseFloat(move.secondary_uom_factor) : 1;
        const ourPrimary = move.our_quantity != null ? parseFloat(move.our_quantity) : null;
        let displayQty;
        if (editedRaw !== undefined && !isNaN(parseFloat(editedRaw))) {
            displayQty = parseFloat(editedRaw);
        } else if (ourPrimary != null) {
            displayQty = isSecondary ? (ourPrimary / factor) : ourPrimary;
        } else {
            displayQty = 0;
        }
        const displayUom = isSecondary ? (move.secondary_uom_name || '') : (move.uom_name || '');

        const esc = (s) => String(s ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const productName = esc(move.product_name || '-');
        const customerName = esc(detailOrder?.intestatario_name || detailOrder?.partner_name || '-');
        const qtyStr = esc(new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(displayQty));
        const uomStr = esc(displayUom || 'Quantita');
        const barcodeJs = JSON.stringify(move.barcode);

        const win = window.open('', '_blank', 'width=900,height=750');
        if (!win) {
            showToast("error", "Popup bloccato dal browser. Abilita i popup per stampare.");
            return;
        }

        const html = `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="UTF-8">
<title>Etichetta - ${productName}</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
<style>
    @page { size: A6 landscape; margin: 4mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; color: #000; background: #f3f3f3; }
    .label {
        width: 800px;
        margin: 20px auto;
        padding: 16px 20px;
        background: white;
        border: 1px solid #ccc;
    }
    .header {
        display: flex;
        align-items: center;
        gap: 20px;
        border-bottom: 1px solid #000;
        padding-bottom: 12px;
        margin-bottom: 14px;
    }
    .header-logo { width: 200px; flex-shrink: 0; }
    .header-logo img { width: 100%; height: auto; display: block; }
    .header-info { flex: 1; font-size: 13px; line-height: 1.5; }
    .header-info .title { font-weight: 700; margin-bottom: 2px; }
    .body-fields { margin-bottom: 12px; }
    .body-fields > div { margin-bottom: 6px; }
    .product-name {
        font-size: 28px;
        font-weight: 800;
        display: inline-block;
        padding: 3px 8px;
    }
    .field {
        font-size: 16px;
        display: inline-block;
        padding: 2px 6px;
    }
    .barcode-area { text-align: center; margin: 14px 0 18px; }
    .barcode-area svg { max-width: 100%; height: auto; }
    .footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        border-top: 1px solid #000;
        padding-top: 10px;
    }
    .footer-cert { width: 400px; }
    .footer-cert img { width: 100%; height: auto; display: block; }
    .footer-recycle { width: 140px; }
    .footer-recycle img { width: 100%; height: auto; display: block; }
    @media print {
        body { background: white; }
        .label { margin: 0; border: none; }
    }
</style>
</head>
<body>
<div class="label">
    <div class="header">
        <div class="header-logo"><img src="/etichetta/local-logo.png" alt="LOCAL toyou"></div>
        <div class="header-info">
            <div class="title">DISTRIBUITO DA:</div>
            <div>LA FRATERNITA' SCS ARL ONLUS</div>
            <div>Via Galileo Galilei 24 - 40064 Ozzano dell'Emilia (BO)</div>
            <div>P.iva/C.F 02202680407 - BDNOO 0125311</div>
        </div>
    </div>
    <div class="body-fields">
        <div><span class="product-name">${productName}</span></div>
        <div><span class="field">${uomStr}: ${qtyStr} (consegnati)</span></div>
        <div><span class="field">Origine ITALIA Cat. 1 (campo certificazione)</span></div>
        <div><span class="field">${customerName}</span></div>
    </div>
    <div class="barcode-area"><svg id="barcode"></svg></div>
    <div class="footer">
        ${move.has_bio_tag
            ? '<div class="footer-cert"><img src="/etichetta/cert-bio.jpg" alt="ICEA Biologico"></div>'
            : '<div></div>'}
        <div class="footer-recycle"><img src="/etichetta/cert-recycle.jpg" alt="PP/LDPE"></div>
    </div>
</div>
<script>
    function renderAndPrint() {
        try {
            JsBarcode("#barcode", ${barcodeJs}, {
                format: "CODE128",
                width: 2,
                height: 80,
                displayValue: true,
                fontSize: 14,
                margin: 10
            });
        } catch (e) { console.error("Barcode error:", e); }
        var imgs = Array.from(document.images);
        var loaded = 0;
        function tryPrint() {
            loaded++;
            if (loaded >= imgs.length) {
                setTimeout(function() { window.print(); }, 150);
            }
        }
        if (imgs.length === 0) {
            setTimeout(function() { window.print(); }, 150);
        } else {
            imgs.forEach(function(img) {
                if (img.complete) tryPrint();
                else {
                    img.addEventListener('load', tryPrint);
                    img.addEventListener('error', tryPrint);
                }
            });
        }
    }
    if (document.readyState === 'complete') renderAndPrint();
    else window.addEventListener('load', renderAndPrint);
<\/script>
</body>
</html>`;

        win.document.open();
        win.document.write(html);
        win.document.close();
    };

    // Format helpers
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const formatQuantity = (val) => {
        if (val == null) return '-';
        return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(val);
    };

    const SortIcon = ({ column }) => (
        <span className={`sort-icon ${sortKey === column ? 'active' : ''}`}>
            {sortKey === column ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ' \u2195'}
        </span>
    );

    return (
        <div className="gestione-ordini-modern">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>

                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Gestione Ordini</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Monitoraggio e gestione degli ordini di consegna da Odoo
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni Gestione Ordini</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Visualizza e gestisce gli <strong>ordini di consegna</strong> provenienti da <strong>Odoo</strong>.
                                    Vengono mostrati solo i picking con riferimento <strong>LOCAL/OUT/</strong> esclusi quelli
                                    in stato bozza o annullati.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>I tre stati di lavorazione</h4>
                                <p>
                                    Ogni ordine ha uno stato logico che riassume i sei stati interni di Odoo:
                                </p>
                                <ul style={{margin: '8px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#64748B', lineHeight: 1.7}}>
                                    <li><strong>Da fare</strong>: ordine non ancora lavorato (nessuna quantita salvata).</li>
                                    <li><strong>Da completare</strong>: il magazziniere ha iniziato a inserire le quantita.
                                        Se vengono azzerate tutte, l'ordine torna automaticamente a "Da fare".</li>
                                    <li><strong>Completato</strong>: ordine convalidato (Odoo state = done). Stato finale.</li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Filtri</h4>
                                <p>
                                    Il filtro <strong>stato</strong> e multi-selezione: puoi combinare a piacere "Da fare", "Da completare"
                                    e "Completato". Di default sono selezionati i primi due (i completati restano nascosti).
                                </p>
                                <p>
                                    Le date <strong>Da</strong>/<strong>A</strong> filtrano sulla data programmata del picking;
                                    di default sono entrambe impostate a oggi. La <strong>ricerca testuale</strong> trova per
                                    riferimento, intestatario, indirizzo di consegna e documento d'origine.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Dettaglio ordine</h4>
                                <p>
                                    Cliccando su un ordine si apre il dettaglio con la lista dei <strong>prodotti</strong>.
                                    Per ogni prodotto inserisci la <strong>quantita effettiva</strong> pesata o contata.
                                    L'input parte sempre da <strong>0</strong> (anche se Odoo aveva valori preesistenti) e
                                    la riga diventa <strong>verde</strong> quando inserisci una quantita maggiore di zero.
                                </p>
                                <p>
                                    Se il prodotto ha un'<strong>unita di misura secondaria</strong> attiva (es. cassetta, collo),
                                    "Richiesto" e "UdM" la mostrano automaticamente e l'input accetta valori in quella stessa unita.
                                    La conversione verso l'unita primaria di Odoo avviene in automatico al salvataggio.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Salva in bozza</h4>
                                <p>
                                    Il salvataggio (singola riga via la spunta verde, oppure batch via il pulsante <strong>Salva in bozza</strong>
                                    nell'intestazione del modale) <strong>NON</strong> aggiorna Odoo: scrive solo nel database della webapp.
                                    Da quel momento l'ordine passa in <strong>Da completare</strong> e i valori restano salvati anche
                                    se chiudi e riapri il modale.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Convalida</h4>
                                <p>
                                    Il pulsante <strong>Convalida</strong> chiude l'ordine: scrive su Odoo le quantita salvate (le
                                    righe a 0 vengono confermate a 0), chiama la convalida del picking <strong>senza generare residuo
                                    (backorder)</strong> e Odoo passa in stato <em>done</em>. L'ordine appare cosi come <strong>Completato</strong>.
                                </p>
                                <p>
                                    Se ci sono modifiche non ancora salvate, un avviso ricorda di salvare prima. Se ci sono righe
                                    con quantita 0, viene chiesta una conferma prima di procedere.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Stampa etichetta prodotto</h4>
                                <p>
                                    Per ogni riga del dettaglio, il pulsante <strong>barcode</strong> apre una pagina stampabile
                                    con l'etichetta LOCAL toyou: nome prodotto, quantita consegnata, intestatario e codice a barre.
                                    La stampa parte automaticamente.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="content-area">
                {loading && data.length === 0 ? (
                    <Lf23Loader testo="Caricamento ordini in corso..." />
                ) : (
                    <>
                        {/* Controls */}
                        <div className="controls-compact">
                            <div className="controls-left">
                                <button
                                    className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                    </svg>
                                    {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </button>
                                <div className="results-badge">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="1" y="3" width="15" height="13"></rect>
                                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                    </svg>
                                    {total} ordini
                                </div>
                            </div>
                            <div className="controls-right">
                                <div className="search-box">
                                    <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                    <input
                                        className="search-input"
                                        type="text"
                                        placeholder="Cerca ordine, intestatario, contatto..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Filter Panel */}
                        {showFilters && (
                            <div className="filter-panel">
                                <div className="filter-grid">
                                    <div className="filter-group">
                                        <label>Stato</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="filter-select"
                                            options={STATE_FILTER_OPTIONS}
                                            value={selectedStateFilter}
                                            onChange={(val) => setSelectedStateFilter(val || [])}
                                            placeholder="Tutti gli stati"
                                            closeMenuOnSelect={false}
                                            isSearchable={false}
                                            noOptionsMessage={() => "Nessuna opzione"}
                                        />
                                    </div>
                                    <div className="filter-group">
                                        <label>Da</label>
                                        <DatePickerModal
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            max={dateTo || undefined}
                                            label="Da"
                                            className="filter-input"
                                        />
                                    </div>
                                    <div className="filter-group">
                                        <label>A</label>
                                        <DatePickerModal
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            min={dateFrom || undefined}
                                            label="A"
                                            className="filter-input"
                                        />
                                    </div>
                                </div>
                                <div className="filter-actions">
                                    <button className="filter-reset-btn" onClick={handleResetFilters}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10"></polyline>
                                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                                        </svg>
                                        Reset
                                    </button>
                                    <button className="filter-apply-btn" onClick={handleApplyFilters}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Applica
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Table */}
                        {sortedData.length > 0 ? (
                            <>
                                {/* Desktop Table */}
                                <div className="table-container-compact desktop-only">
                                    <div className="table-wrapper">
                                        <table className="data-table-compact">
                                            <thead>
                                                <tr>
                                                    <th onClick={() => handleSort('name')}>
                                                        <div className="th-content">Riferimento <SortIcon column="name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('intestatario_name')}>
                                                        <div className="th-content">Intestatario <SortIcon column="intestatario_name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('partner_name')}>
                                                        <div className="th-content">Indirizzo consegna <SortIcon column="partner_name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('scheduled_date')}>
                                                        <div className="th-content">Data programmata <SortIcon column="scheduled_date" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('origin')}>
                                                        <div className="th-content">Doc. di origine <SortIcon column="origin" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('company_name')}>
                                                        <div className="th-content">Azienda <SortIcon column="company_name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('display_state')}>
                                                        <div className="th-content">Stato <SortIcon column="display_state" /></div>
                                                    </th>
                                                    <th style={{textAlign:'center'}}>Dettaglio</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedData.map((row, idx) => (
                                                    <tr key={`${row.id}-${idx}`}>
                                                        <td className="reference-cell"><strong>{row.name || '-'}</strong></td>
                                                        <td>{row.intestatario_name || '-'}</td>
                                                        <td>{row.partner_name || '-'}</td>
                                                        <td>{formatDate(row.scheduled_date)}</td>
                                                        <td>{row.origin || '-'}</td>
                                                        <td>{row.company_name || '-'}</td>
                                                        <td>
                                                            <span className={`state-badge ${STATE_CLASSES[row.display_state] || ''}`}>
                                                                {STATE_LABELS[row.display_state] || row.display_state || '-'}
                                                            </span>
                                                        </td>
                                                        <td style={{textAlign:'center'}}>
                                                            <button
                                                                className="detail-btn"
                                                                onClick={() => handleOpenDetail(row)}
                                                                title="Vedi dettaglio"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                                    <circle cx="12" cy="12" r="3"></circle>
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Mobile Card View */}
                                <div className="mobile-cards-container mobile-only">
                                    {sortedData.map((row, idx) => (
                                        <div key={`card-${row.id}-${idx}`} className="mobile-card" onClick={() => handleOpenDetail(row)}>
                                            <div className="mobile-card-header">
                                                <div className="mobile-card-title">{row.name || '-'}</div>
                                                <span className={`state-badge ${STATE_CLASSES[row.display_state] || ''}`}>
                                                    {STATE_LABELS[row.display_state] || row.display_state || '-'}
                                                </span>
                                            </div>
                                            <div className="mobile-card-body">
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Intestatario</span>
                                                    <span className="mobile-card-value">{row.intestatario_name || '-'}</span>
                                                </div>
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Indirizzo consegna</span>
                                                    <span className="mobile-card-value">{row.partner_name || '-'}</span>
                                                </div>
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Data</span>
                                                    <span className="mobile-card-value">{formatDate(row.scheduled_date)}</span>
                                                </div>
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Doc. origine</span>
                                                    <span className="mobile-card-value">{row.origin || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                <div className="pagination-compact">
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6"/>
                                        </svg>
                                        Prec
                                    </button>
                                    <div className="pagination-info-compact">
                                        <span className="pagination-text-compact">
                                            Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                        </span>
                                        <span className="pagination-total-compact">({total} risultati totali)</span>
                                    </div>
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === totalPages || totalPages === 0}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Succ
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6"/>
                                        </svg>
                                    </button>

                                    <div className="items-per-page-compact">
                                        <span className="items-label-compact">Elementi per pagina:</span>
                                        <div className="items-buttons-compact">
                                            {[10, 20, 50, 100].map((num) => (
                                                <button
                                                    key={num}
                                                    className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                    onClick={() => {
                                                        setLimit(num);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="empty-state">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                                    <rect x="1" y="3" width="15" height="13"></rect>
                                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                </svg>
                                <p>Nessun ordine di consegna trovato</p>
                                <span>Prova a modificare i filtri o il periodo di ricerca</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Detail Modal */}
            {detailModalOpen && detailOrder && (
                <div className="modal-overlay" onClick={handleCloseDetail}>
                    <div className="modal-container modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="1" y="3" width="15" height="13"></rect>
                                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                </svg>
                            </div>
                            <h3>Dettaglio Ordine: {detailOrder.name}</h3>
                            <div className="modal-header-actions">
                                <button
                                    className="header-action-btn save-draft-btn"
                                    onClick={handleSaveDraft}
                                    disabled={savingDraft || validating || Object.keys(editedQuantities).length === 0 || detailOrder.display_state === 'completato'}
                                    title="Salva tutte le quantita editate in bozza"
                                >
                                    {savingDraft ? 'Salvataggio...' : 'Salva in bozza'}
                                </button>
                                <button
                                    className="header-action-btn validate-btn"
                                    onClick={handleValidate}
                                    disabled={savingDraft || validating || detailOrder.display_state === 'completato'}
                                    title="Convalida l'ordine su Odoo"
                                >
                                    {validating ? 'Convalida...' : 'Convalida'}
                                </button>
                            </div>
                            <button className="modal-close" onClick={handleCloseDetail}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* Order info */}
                            <div className="detail-info-grid">
                                <div className="detail-info-item">
                                    <span className="detail-info-label">Intestatario</span>
                                    <span className="detail-info-value">{detailOrder.intestatario_name || '-'}</span>
                                </div>
                                <div className="detail-info-item">
                                    <span className="detail-info-label">Indirizzo consegna</span>
                                    <span className="detail-info-value">{detailOrder.partner_name || '-'}</span>
                                </div>
                                <div className="detail-info-item">
                                    <span className="detail-info-label">Data programmata</span>
                                    <span className="detail-info-value">{formatDate(detailOrder.scheduled_date)}</span>
                                </div>
                                <div className="detail-info-item">
                                    <span className="detail-info-label">Doc. di origine</span>
                                    <span className="detail-info-value">{detailOrder.origin || '-'}</span>
                                </div>
                                <div className="detail-info-item">
                                    <span className="detail-info-label">Stato</span>
                                    <span className={`state-badge ${STATE_CLASSES[detailOrder.display_state] || ''}`}>
                                        {STATE_LABELS[detailOrder.display_state] || detailOrder.display_state || '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Moves table */}
                            {detailLoading ? (
                                <div className="detail-loading">
                                    <Lf23Loader testo="Caricamento prodotti..." />
                                </div>
                            ) : detailMoves.length > 0 ? (
                                <div className="detail-table-wrapper">
                                    <table className="detail-table">
                                        <thead>
                                            <tr>
                                                <th>Prodotto</th>
                                                <th>Richiesto</th>
                                                <th>UdM</th>
                                                <th>Quantita</th>
                                                <th>In Kg</th>
                                                <th style={{textAlign:'center'}}>Barcode</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailMoves.map((move) => {
                                                // Modalita unita: secondaria attiva se la move ha secondary_uom_qty > 0
                                                const isSecondary = parseFloat(move.secondary_uom_qty || 0) > 0;
                                                const factor = parseFloat(move.secondary_uom_factor) > 0 ? parseFloat(move.secondary_uom_factor) : 1;
                                                const displayedDemand = isSecondary ? move.secondary_uom_qty : move.product_uom_qty;
                                                const displayedUom = isSecondary ? move.secondary_uom_name : move.uom_name;

                                                const editedRaw = editedQuantities[move.id];
                                                // editedRaw e' SEMPRE espresso nell'unita visualizzata (secondaria se isSecondary)
                                                const editedDisplay = editedRaw !== undefined ? parseFloat(editedRaw) : NaN;
                                                const editedPrimary = !isNaN(editedDisplay)
                                                    ? (isSecondary ? editedDisplay * factor : editedDisplay)
                                                    : NaN;

                                                // our_quantity nel DB e' SEMPRE in primaria. Per il display la convertiamo in secondaria se serve.
                                                const ourPrimary = move.our_quantity != null ? parseFloat(move.our_quantity) : null;
                                                const ourDisplay = ourPrimary != null
                                                    ? (isSecondary ? ourPrimary / factor : ourPrimary)
                                                    : null;

                                                const isFilled = (!isNaN(editedPrimary) && editedPrimary > 0) || (ourPrimary != null && ourPrimary > 0);
                                                const inputValue = editedRaw !== undefined
                                                    ? editedRaw
                                                    : (ourDisplay != null ? ourDisplay : 0);

                                                // Conversione dinamica della Quantita in Kg.
                                                // Si attiva quando la move usa la UDM secondaria (es. Pz) e la UDM primaria
                                                // del prodotto e' "kg": in quel caso il valore inserito moltiplicato per
                                                // il factor della secondaria (da product.secondary.unit) da' direttamente i kg.
                                                // Per i prodotti con primaria diversa da kg (es. Units) il dato non e'
                                                // ricavabile da Odoo (weight non valorizzato).
                                                const primaryIsKg = (move.uom_name || '').trim().toLowerCase() === 'kg';
                                                const inputDisplayNum = parseFloat(inputValue);
                                                const showConversion = isSecondary && primaryIsKg;
                                                const conversionValue = showConversion && !isNaN(inputDisplayNum)
                                                    ? inputDisplayNum * factor
                                                    : null;
                                                return (
                                                <tr key={move.id} className={isFilled ? 'row-filled' : ''}>
                                                    <td className="product-name-cell">{move.product_name || '-'}</td>
                                                    <td>{formatQuantity(displayedDemand)}</td>
                                                    <td>{displayedUom || '-'}</td>
                                                    <td className="quantity-edit-cell">
                                                        <div className="quantity-edit-group">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="quantity-input"
                                                                value={inputValue}
                                                                onChange={(e) => handleQuantityChange(move.id, e.target.value)}
                                                                onFocus={(e) => e.target.select()}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveQuantity(move); }}
                                                            />
                                                            {editedQuantities[move.id] !== undefined && (
                                                                <button
                                                                    className="save-qty-btn"
                                                                    onClick={() => handleSaveQuantity(move)}
                                                                    disabled={savingMoves[move.id]}
                                                                    title="Salva quantita"
                                                                >
                                                                    {savingMoves[move.id] ? (
                                                                        <span className="saving-spinner"></span>
                                                                    ) : (
                                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                            <path d="M20 6L9 17l-5-5"/>
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="secondary-conversion-cell">
                                                        {showConversion
                                                            ? `${formatQuantity(conversionValue || 0)} Kg`
                                                            : '—'}
                                                    </td>
                                                    <td style={{textAlign:'center'}}>
                                                        <button
                                                            className="barcode-btn"
                                                            disabled={!move.barcode}
                                                            title={move.barcode ? `Stampa barcode: ${move.barcode}` : "Nessun barcode disponibile"}
                                                            onClick={() => handlePrintBarcode(move)}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="3" y="3" width="4" height="18"></rect>
                                                                <rect x="9" y="3" width="2" height="18"></rect>
                                                                <rect x="13" y="3" width="4" height="18"></rect>
                                                                <rect x="19" y="3" width="2" height="18"></rect>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="detail-empty">
                                    <p>Nessun prodotto trovato per questo ordine</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            {confirmDialog && (
                <div className="modal-overlay" onClick={confirmDialog.onCancel || confirmDialog.onConfirm}>
                    <div className="modal-container confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className={`modal-icon ${confirmDialog.kind === 'warning' ? 'icon-warning' : 'icon-info'}`}>
                                {confirmDialog.kind === 'warning' ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                )}
                            </div>
                            <h3>{confirmDialog.title}</h3>
                        </div>
                        <div className="modal-body">
                            <p className="confirm-message">{confirmDialog.message}</p>
                        </div>
                        <div className="confirm-actions">
                            {confirmDialog.cancelLabel && (
                                <button className="confirm-cancel-btn" onClick={confirmDialog.onCancel}>
                                    {confirmDialog.cancelLabel}
                                </button>
                            )}
                            <button
                                className={`confirm-ok-btn ${confirmDialog.kind === 'warning' ? 'confirm-ok-warning' : ''}`}
                                onClick={confirmDialog.onConfirm}
                            >
                                {confirmDialog.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`toast-notification toast-${toast.type}`}>
                    <div className="toast-icon">
                        {toast.type === 'success' ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                        )}
                    </div>
                    <span className="toast-message">{toast.message}</span>
                </div>
            )}
        </div>
    );
};

export default GestioneOrdini;
